import { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { Request, RequestType } from "../../models/Request";
import { Product, ProductType } from "../../models/Product";
import { Partner } from "../../models/Partner";
import { City } from "../../models/City";
import { RoleType } from "../../models/Role";
import { activeUser } from "../../services/AuthService";
import {
  getAllDocuments,
  getProductReservations,
  getMercantileAvailableSales,
  addNewRequest,
  updateOneRequest,
  getOneRequest,
} from "../../services/NewVersionService";
import {
  getAllCities,
  createCity,
  updateOneCity,
} from "../../services/CityService";
import { getAllPartners } from "../../services/PartnerService";
import { getAllProducts } from "../../services/ProductService";
import { getAllWarehouses } from "../../services/WarehouseService";
import "./NewMercantileRequestForm.scss";

export interface NewMercantileRequestFormProps {
  show?: string;
  request?: Request;
  buttonLabel?: string;
  buttonIcon?: string;
  buttonStyle?: React.CSSProperties;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewCityStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
  refreshingData?: () => void;
}

export const NewMercantileRequestForm = (p: NewMercantileRequestFormProps) => {
  const {
    request,
    show,
    buttonLabel,
    buttonIcon,
    buttonStyle,
    buttonSaveStyle,
    buttonNewCityStyle,
    buttonResetStyle,
    refreshingData,
  } = p;

  const { t } = useTranslation();

  const [visibleNewMercantileRequestForm, setVisibleNewMercantileRequestForm] =
    useState<boolean>(false);
  const [newMercantileRequest, setNewMercantileRequest] = useState<Request>({
    id: request?.id ?? "",
    type: request?.type ?? RequestType.Import,
    product: request?.product ?? ({} as Product),
    year: request?.year ?? new Date().getFullYear(),
    partner: request?.partner ?? ({} as Partner),
    place: request?.place ?? "",
    inputCity: request?.inputCity ?? undefined,
    inputWarehouse: request?.inputWarehouse ?? undefined,
    amount: request?.amount ?? undefined,
    date: new Date(request?.date as Date) ?? undefined,
    transportCost: request?.transportCost ?? true,
    contact: request?.contact ?? "",
    note: request?.note ?? "",
    requestConfirmed: false,
  });
  const [partnerOptions, setPartnerOptions] = useState<Partner[]>([]);
  const [visibleNewCityAddForm, setVisibleNewCityAddForm] =
    useState<boolean>(false);
  const [newCity, setNewCity] = useState<City>({
    code: "",
    name: "",
    id: "",
  });
  const [cities, setCities] = useState<City[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [mercantileInputsApi, setMercantileInputsApi] = useState<any[]>([]);
  const [mercantileOutputsApi, setMercantileOutputsApi] = useState<any[]>([]);
  const [productReservationsApi, setProductReservationsApi] = useState<any[]>(
    [],
  );
  const [maxAmount, setMaxAmount] = useState<number>(0); //maksimalno dostupna kolicina za selektovani magacin i selektovanu robu
  const toast = useRef(null);

  const [maxAmountSB, setMaxAmountSB] = useState<number | null>(null); //todo, remove...

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  useEffect(() => {
    const load = async () => {
      try {
        const currentYear = new Date().getFullYear();
        const dateFrom = new Date(currentYear, 0, 1, 0, 0, 1);
        const dateTo = new Date();

        const [
          citiesResp,
          partnersResp,
          productsResp,
          warehousesResp,
          inputsResp,
          outputsResp,
          reservationsResp,
        ] = await Promise.all([
          getAllCities(),
          getAllPartners(),
          getAllProducts(),
          getAllWarehouses(),
          getAllDocuments(true, true, dateFrom, dateTo),
          getAllDocuments(true, false, dateFrom, dateTo),
          getProductReservations(currentYear, dateFrom, dateTo),
        ]);

        const ca: City[] = citiesResp?.data?.result ?? [];
        const pa: Partner[] = partnersResp?.data?.result ?? [];
        const pr: Product[] = productsResp?.data?.result ?? [];
        const wh: any[] = warehousesResp?.data?.result ?? [];

        setCities(ca);
        setPartners(pa);
        setProducts(pr);
        setWarehouses(wh);

        setMercantileInputsApi(inputsResp?.data?.result ?? []);
        setMercantileOutputsApi(outputsResp?.data?.result ?? []);
        setProductReservationsApi(reservationsResp?.data?.result ?? []);

        // default date
        setNewMercantileRequest((prev) => ({
          ...prev,
          date: prev?.date ? new Date(prev.date as any) : new Date(),
        }));

        // partnerOptions (komercijalista vidi samo svoje partnere)
        if (activeUser?.role?.name === RoleType.Commercialist) {
          const fullName = `${activeUser?.firstName} ${activeUser?.lastName}`;
          setPartnerOptions(pa.filter((x) => x.commercialist1 === fullName));
        } else {
          setPartnerOptions(pa);
        }
      } catch (e) {
        console.log("Init mercantile request form failed", e);
      }
    };

    load();
  }, []);

  useEffect(() => {
    if (newMercantileRequest.id) {
      getOneRequest(newMercantileRequest.id)
        .then((resp) => {
          const req = resp?.data?.result;
          if (req?.date) req.date = new Date(req.date);
          if (req) setNewMercantileRequest(req);
        })
        .then(() => calculateMaxAmount())
        .catch((e) => console.log("getOneRequest failed", e));
    }
  }, [newMercantileRequest.id]);

  useEffect(() => {
    if (
      newMercantileRequest.inputWarehouse?.name !== undefined &&
      newMercantileRequest.product?.name !== undefined
    ) {
      calculateMaxAmount();
    }
  }, [newMercantileRequest.inputWarehouse, newMercantileRequest.product]);

  useEffect(() => {
    if (show) {
      setVisibleNewMercantileRequestForm(true);
    }
  }, [show]);

  useEffect(() => {
    if (request) {
      setNewMercantileRequest(request);
      reset();
    }
  }, [request]);

  useEffect(() => {}, [newMercantileRequest]);

  const showModal = () => {
    setVisibleNewMercantileRequestForm(
      !visibleNewMercantileRequestForm ? true : false,
    );
  };

  const calculateMaxAmountFromStockBalance = async (oldMax?: number) => {
    const businessYear = new Date().getFullYear(); // isto kao što već radiš u useEffect (currentYear)
    const warehouseId = newMercantileRequest.inputWarehouse?.id;
    const productId = newMercantileRequest.product?.id;

    if (!warehouseId || !productId) return;

    try {
      const resp = await getMercantileAvailableSales({
        year: businessYear,
        warehouseId,
        productId,
      });

      const available = Number(resp?.data?.result?.available ?? 0);
      setMaxAmountSB(available);

      console.log("[COMPARE][Mercantile available]", {
        businessYear,
        warehouseId,
        productId,
        oldMax,
        stockBalanceAvailable: available,
        diff: (oldMax ?? 0) - available,
      });
    } catch (e) {
      console.log("[StockBalance] getMercantileAvailableSales failed", e);
    }
  };

  const calculateMaxAmount = () => {
    let newMaxAmount: number = 0;
    for (const mercantileInput of mercantileInputsApi) {
      const isFinished =
        mercantileInput?.finishedDocument === true ||
        mercantileInput?.isDocumentFinished === true;
      const warehouseName =
        mercantileInput?.warehouse?.name ?? mercantileInput?.warehouseName;
      const productName =
        mercantileInput?.product?.name ?? mercantileInput?.productName;
      if (
        isFinished && // zaključen dokument
        warehouseName === newMercantileRequest.inputWarehouse?.name && // u izabranom magacinu
        productName === newMercantileRequest.product?.name // za izabrani proizvod
      ) {
        newMaxAmount += mercantileInput?.netto ?? 0;
      }
    }
    for (const mercantileOutput of mercantileOutputsApi) {
      const isFinished =
        mercantileOutput?.finishedDocument === true ||
        mercantileOutput?.isDocumentFinished === true;

      const warehouseName =
        mercantileOutput?.warehouse?.name ?? mercantileOutput?.warehouseName;
      const productName =
        mercantileOutput?.product?.name ?? mercantileOutput?.productName;
      if (
        isFinished &&
        warehouseName === newMercantileRequest.inputWarehouse?.name &&
        productName === newMercantileRequest.product?.name
      ) {
        newMaxAmount -= mercantileOutput?.netto ?? 0;
      }
    }
    for (const productReservation of productReservationsApi) {
      const warehouseName =
        productReservation?.warehouse?.name ??
        productReservation?.warehouseName;
      const productName =
        productReservation?.product?.name ?? productReservation?.productName;
      const expiryDate = productReservation?.expiryDate
        ? new Date(productReservation.expiryDate)
        : undefined;
      if (
        productReservation.type === ProductType.MercantileGoods &&
        warehouseName === newMercantileRequest.inputWarehouse?.name &&
        productName === newMercantileRequest.product?.name &&
        expiryDate &&
        expiryDate.getTime() > new Date().getTime() // samo važeće rezervacije
      ) {
        newMaxAmount -= productReservation?.amount ?? 0;
      }
    }
    calculateMaxAmountFromStockBalance(newMaxAmount);
    setMaxAmount(newMaxAmount);
  };
  console.log(maxAmountSB);
  const handleNewMercantileRequestForm = (prop: string, value: any) => {
    setNewMercantileRequest((newMercantileRequest) => ({
      ...newMercantileRequest,
      [prop]: value,
    }));
  };

  const handleRequestTypeChange = (prop: string, value: any) => {
    let copyOfNewMercantileRequest = cloneDeep(newMercantileRequest);
    copyOfNewMercantileRequest.type = value;
    if (value === RequestType.Import) {
      copyOfNewMercantileRequest.partner = {} as Partner;
    }
    if (value === RequestType.Relocation) {
      copyOfNewMercantileRequest.partner = partners.find(
        (item) => item.name == "RWA Srbija doo",
      );
    }
    setNewMercantileRequest(copyOfNewMercantileRequest);
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewMercantileRequest = cloneDeep(newMercantileRequest);
    copyOfNewMercantileRequest.product = products.find(
      (item) => item.id == value,
    );
    setNewMercantileRequest(copyOfNewMercantileRequest);
  };

  const handlePartnerChange = (prop: string, value: any) => {
    let copyOfNewMercantileRequest = cloneDeep(newMercantileRequest);
    copyOfNewMercantileRequest.partner = partners.find(
      (item) => item.id == value,
    );
    setNewMercantileRequest(copyOfNewMercantileRequest);
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewMercantileRequest = cloneDeep(newMercantileRequest);
    copyOfNewMercantileRequest.inputWarehouse = warehouses.find(
      (item) => item.id == value,
    );
    copyOfNewMercantileRequest.inputCity = undefined;
    copyOfNewMercantileRequest.place = warehouses.find(
      (item) => item.id == value,
    )?.name;
    setNewMercantileRequest(copyOfNewMercantileRequest);
  };

  const handleCityChange = (prop: string, value: any) => {
    let copyOfNewMercantileRequest = cloneDeep(newMercantileRequest);
    copyOfNewMercantileRequest.inputCity = cities.find(
      (item) => item.id == value,
    );
    copyOfNewMercantileRequest.inputWarehouse = undefined;
    copyOfNewMercantileRequest.place = cities.find(
      (item) => item.id == value,
    )?.name;
    setNewMercantileRequest(copyOfNewMercantileRequest);
  };

  const handleNewCity = (prop: string, value: string) => {
    prop === "newCityName"
      ? setNewCity((newCity) => ({ ...newCity, name: value }))
      : setNewCity((newCity) => ({ ...newCity, code: value }));
  };

  const addNewCity = () => {
    const save = async () => {
      try {
        if (newCity.id) {
          await updateOneCity(newCity.id, {
            name: newCity.name,
            code: newCity.code,
          });
        } else {
          const code = new Date().getTime().toFixed();
          await createCity({ name: newCity.name, code });
        }

        const citiesResp = await getAllCities();
        setCities(citiesResp?.data?.result ?? []);
      } catch (e) {
        console.log("add/update city failed", e);
      } finally {
        setVisibleNewCityAddForm(false);
        setNewCity({ id: "", code: "", name: "" });
      }
    };
    save();
  };

  const onSubmit = () => {
    let copyOfNewMercantileRequest = cloneDeep(newMercantileRequest);
    let day = new Date(newMercantileRequest?.date as Date).getDate();
    if (!(day >= 1 && day <= 31)) copyOfNewMercantileRequest.date = new Date();
    if (
      (newMercantileRequest.type === RequestType.Export ||
        newMercantileRequest.type === RequestType.Relocation) &&
      maxAmount < (newMercantileRequest.amount ?? 0)
    ) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        summary: t(`Neodgovarajuća količina`),
        detail: t(
          `Uneta količina proizvoda je veća od maksimalno dostupne količine u selektovanom magacinu. Maksimalno dostupna količina za izabrani proizvod je ${maxAmount}`,
        ),
        sticky: true,
      });
      return;
    }
    const save = async () => {
      try {
        if (newMercantileRequest.id) {
          await updateOneRequest(
            newMercantileRequest.id,
            copyOfNewMercantileRequest,
          );
        } else {
          const payload: any = cloneDeep(copyOfNewMercantileRequest);
          // bitno: na create ne šaljemo id = "" (uuid crash na BE)
          delete payload.id;
          await addNewRequest(payload);
        }

        if (refreshingData) refreshingData();

        //@ts-ignore
        toast.current.show({
          severity: "success",
          detail: t(`Uspešno dodati novi podaci`),
          life: 3000,
        });
      } catch (e) {
        console.log("save request failed", e);
        //@ts-ignore
        toast.current.show({
          severity: "error",
          detail: t(`Došlo je do greške pri snimanju`),
          life: 3000,
        });
      } finally {
        handleResetModal();
      }
    };

    save();
    handleResetModal();
  };

  const onError = () => {
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const handleResetModal = () => {
    setNewMercantileRequest({
      id: "",
      type: RequestType.Import,
      product: {} as Product,
      year: new Date().getFullYear(),
      partner: {} as Partner,
      place: "",
      amount: undefined,
      date: undefined,
      transportCost: true,
      note: "",
      requestConfirmed: false,
    });
    showModal();
  };

  const isMercantile = (product: Product) => {
    return product.type === ProductType.MercantileGoods;
  };

  let addButtonStyle: React.CSSProperties = {};
  if (buttonStyle) {
    addButtonStyle = buttonStyle;
  } else {
    addButtonStyle.background = "rgba(0, 165, 83, 1)";
    addButtonStyle.border = "none";
  }

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  let addButtonNewCityStyle: React.CSSProperties = {};
  if (buttonNewCityStyle) {
    addButtonNewCityStyle = buttonNewCityStyle;
  } else {
    addButtonNewCityStyle.background = "rgba(21, 115, 71, 1)";
    addButtonNewCityStyle.border = "none";
  }

  const getHeaderClass = () => {
    switch (newMercantileRequest.type) {
      case RequestType.Import:
        return "p-dialog-header p-dialog-header-import";
      case RequestType.Export:
        return "p-dialog-header p-dialog-header-export";
      case RequestType.Relocation:
        return "p-dialog-header p-dialog-header-relocation";
      default:
        return "p-dialog-header";
    }
  };

  const header = (
    <div
      className={`p-dialog-header ${getHeaderClass()}`}
      style={{ borderBottom: "none" }}
    >
      <span className="p-dialog-titlebar-icon p-link">
        <i className="bi bi-truck" style={{ fontSize: 18 }}></i>
      </span>
      {newMercantileRequest.id === "" ? (
        <span>{t("Kreiranje zahteva merkantile")}</span>
      ) : (
        <span>
          {t("Ažuriranje zahteva merkantile ")}
          {`${newMercantileRequest?.requestNumberFormatted ?? ""}`}
        </span>
      )}
    </div>
  );

  const footer = (
    <div className="footer-buttons">
      <ButtonInput
        labelText={`${t("Kreiraj")}`}
        onButtonClick={handleSubmit(onSubmit, onError)}
        addStyle={addButtonSaveStyle}
        disabled={activeUser?.role?.name === RoleType.Director}
      />
      <ButtonInput
        labelText={`${t("Resetuj")}`}
        onButtonClick={handleResetModal}
        addStyle={addButtonResetStyle}
        disabled={activeUser?.role?.name === RoleType.Director}
      />
    </div>
  );

  return (
    <>
      <ButtonInput
        labelText={buttonLabel as string}
        icon={buttonIcon}
        onButtonClick={showModal}
        addStyle={addButtonStyle}
      />
      <Toast ref={toast} position={"top-right"} />
      {visibleNewMercantileRequestForm ? (
        <div>
          <Dialog
            header={header}
            headerClassName={getHeaderClass()}
            visible={true}
            style={{ width: "50vw" }}
            className="new-mercantile-dialog"
            footer={footer}
            blockScroll={false}
            draggable={false}
            onHide={showModal}
          >
            <div className="new-mercantile-request-form-body">
              <DropdownInput
                id="type"
                value={newMercantileRequest.type as string}
                onValueChange={handleRequestTypeChange}
                options={[
                  { label: t("Prijem"), value: RequestType.Import },
                  { label: t("Prodaja"), value: RequestType.Export },
                  { label: t("Premeštaj"), value: RequestType.Relocation },
                ]}
                labelText={`${t("Tip zahteva")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                colorType={newMercantileRequest.type as string}
              />
              <DropdownInput
                id="product"
                value={newMercantileRequest.product?.id}
                onValueChange={handleProductChange}
                optionValue="id"
                optionLabel="name"
                options={products.filter(isMercantile)}
                filter={true}
                labelText={`${t("Roba")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                colorType={newMercantileRequest.type as string}
              />
              <DropdownInput
                id="partner"
                value={newMercantileRequest.partner?.id}
                onValueChange={handlePartnerChange}
                optionValue="id"
                optionLabel="name"
                options={partnerOptions}
                filter={true}
                labelText={
                  newMercantileRequest.type === RequestType.Export
                    ? `${t("Kupac robe")} *`
                    : newMercantileRequest.type === RequestType.Import
                      ? `${t("Dobavljač robe")} *`
                      : `${t("Vlasnik robe")} *`
                }
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={partnerOptions.length === 0}
                colorType={newMercantileRequest.type as string}
              />
              {newMercantileRequest.type === RequestType.Import && (
                <div className="city-content">
                  <div className="city-id-content">
                    <DropdownInput
                      id="inputCity"
                      value={newMercantileRequest.inputCity?.id}
                      onValueChange={handleCityChange}
                      optionValue="id"
                      optionLabel="name"
                      options={cities}
                      filter={true}
                      labelText={`${t("Polazno mesto")} *`}
                      placeholderText={t("Izaberite iz liste")}
                      control={control}
                      setValue={setValue}
                      errors={errors}
                      rules={{ required: true }}
                      colorType={newMercantileRequest.type as string}
                    />
                  </div>
                  <div className="city-add-button">
                    <ButtonInput
                      labelText={t("Dodaj mesto")}
                      onButtonClick={() => {
                        setVisibleNewCityAddForm(
                          !visibleNewCityAddForm ? true : false,
                        );
                      }}
                      icon="bi bi-plus-square"
                      iconAlign="left"
                      addStyle={addButtonNewCityStyle}
                      disabled={activeUser?.role?.name === RoleType.Director}
                    />
                  </div>
                  {visibleNewCityAddForm && (
                    <div className="new-city-name">
                      <TextInputWithIcon
                        id="newCityName"
                        value={newCity.name}
                        onValueChange={handleNewCity}
                        labelText={t("Naziv mesta/naselja")}
                        icon="bi bi-signpost"
                        iconAlign="left"
                        placeholderText={t("Unesite mesto/naselje")}
                        control={control}
                        errors={errors}
                        colorType={newMercantileRequest.type as string}
                      />
                    </div>
                  )}
                  {visibleNewCityAddForm && (
                    <div className="new-city-add-button">
                      <ButtonInput
                        labelText={t("Dodaj")}
                        onButtonClick={addNewCity}
                        addStyle={addButtonNewCityStyle}
                      />
                    </div>
                  )}
                </div>
              )}
              {(newMercantileRequest.type === RequestType.Export ||
                newMercantileRequest.type === RequestType.Relocation) && (
                <DropdownInput
                  id="inputWarehouse"
                  value={newMercantileRequest.inputWarehouse?.id}
                  onValueChange={handleWarehouseChange}
                  optionValue="id"
                  optionLabel="name"
                  options={warehouses.filter((item) => item.virtual !== true)}
                  labelText={`${
                    newMercantileRequest.type === RequestType.Export
                      ? t("Prijemno mesto")
                      : t("Izlazno mesto")
                  } *`}
                  placeholderText={t("Izaberite iz liste")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{ required: true }}
                  colorType={newMercantileRequest.type as string}
                />
              )}
              {newMercantileRequest.type === RequestType.Import && (
                <div className="place-address">
                  <TextInput
                    id="address"
                    value={newMercantileRequest.address as string}
                    onValueChange={handleNewMercantileRequestForm}
                    labelText={`${t("Adresa polaznog mesta")}`}
                    placeholderText={`${t("Unesite adresu polaznog mesta")}`}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    //rules={{ required: true }}
                    colorType={newMercantileRequest.type as string}
                  />
                </div>
              )}
              <TextInput
                id="contact"
                value={newMercantileRequest.contact ?? ""}
                onValueChange={handleNewMercantileRequestForm}
                labelText={`${t("Kontakt")}`}
                placeholderText={`${t("Unesite kontakt")}`}
                control={control}
                setValue={setValue}
                errors={errors}
                colorType={newMercantileRequest.type as string}
              />
              <div></div>
              <div className="amount-content">
                <div className="amount">
                  <TextInput
                    id="amount"
                    value={newMercantileRequest.amount ?? ""}
                    onValueChange={handleNewMercantileRequestForm}
                    labelText={`
                      ${t("Količina: ")}
                      ${
                        newMercantileRequest.type === RequestType.Export
                          ? `(max: ${maxAmount})`
                          : ""
                      } *`}
                    placeholderText={t("Unesite količinu najave")}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    rules={{
                      required: true,
                      pattern: {
                        value: /^\d+$/,
                        message: "Please enter a number",
                      },
                    }}
                    colorType={newMercantileRequest.type as string}
                  />
                </div>
                <TextInput
                  id="measureUnit"
                  value={newMercantileRequest.product?.measuringUnit as string}
                  onValueChange={() => {}}
                  labelText={`${t("Merna jed.")}`}
                  placeholderText={""}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{}}
                  disabled={true}
                  colorType={newMercantileRequest.type as string}
                />
              </div>
              <DropdownInput
                id="year"
                value={newMercantileRequest.year as number}
                onValueChange={handleNewMercantileRequestForm}
                options={[
                  {
                    label: `${new Date().getFullYear()}.`,
                    value: new Date().getFullYear(),
                  },
                  {
                    label: `${new Date().getFullYear() - 1}.`,
                    value: new Date().getFullYear() - 1,
                  },
                  {
                    label: `${new Date().getFullYear() - 2}.`,
                    value: new Date().getFullYear() - 2,
                  },
                ]}
                filter={false}
                labelText={`${t("Otkupna godina")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                colorType={newMercantileRequest.type as string}
              />
              <div className="date-cost-included-content">
                <div className="date">
                  <DateInput
                    id="date"
                    value={
                      newMercantileRequest.date
                        ? new Date(newMercantileRequest.date)
                        : new Date()
                    }
                    onValueChange={handleNewMercantileRequestForm}
                    labelText={`${t("Izaberite datum")} *`}
                    placeholderText={`${t("Datum")}`}
                    icon="bi bi-calendar-check"
                    minDate={new Date()}
                    control={control}
                    errors={errors}
                    //rules={{ required: true }}
                    colorType={newMercantileRequest.type as string}
                  />
                </div>
                <div className="is-cost-included">
                  <RadioButtonInput
                    id="transportCost"
                    value={newMercantileRequest.transportCost as boolean}
                    radioButtonValue1={t("DA")}
                    radioButtonValue2={t("NE")}
                    onValueChange={handleNewMercantileRequestForm}
                    labelText={t("Trošak prevoza RWA")}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    colorType={newMercantileRequest.type as string}
                  />
                </div>
              </div>
              <TextAreaInput
                id="note"
                value={newMercantileRequest.note as string}
                onValueChange={handleNewMercantileRequestForm}
                rows={5}
                cols={50}
                labelText={`${t("Napomena")}`}
                placeholderText={t("Unesite napomenu")}
                control={control}
                setValue={setValue}
                errors={errors}
                colorType={newMercantileRequest.type as string}
              />
            </div>
          </Dialog>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
